"use client";

import React, { useState } from "react";
import Link from "next/link";
import { Header } from "@/modules/shared/components/Header";
import { Footer } from "@/modules/shared/components/Footer";
import { PageTransition, FadeIn } from "@/modules/shared/components/PageTransition";
import { siteConfig } from "@/config/site.config";

interface ProximamenteTemplateProps {
  titulo: string;
  subtitulo: string;
  temas: string[];
  publicado: boolean;
  childrenRealContent?: React.ReactNode;
}

export const ProximamenteTemplate: React.FC<ProximamenteTemplateProps> = ({
  titulo,
  subtitulo,
  temas,
  publicado,
  childrenRealContent,
}) => {
  const [email, setEmail] = useState("");
  const [aceptaPolitica, setAceptaPolitica] = useState(false);
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [honeypot, setHoneypot] = useState("");

  const handleNotifySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (honeypot) return;
    if (!aceptaPolitica) return;
    if (!email) return;

    setStatus("loading");
    try {
      const formData = new FormData();
      formData.append("email", email);
      formData.append("interes_seccion", titulo);
      formData.append("consentimiento_ley_29733", "Aceptado");
      formData.append("_subject", `Aviso de Publicación: ${titulo}`);
      formData.append("_captcha", "false");

      const res = await fetch(`https://formsubmit.co/ajax/${siteConfig.correoContacto}`, {
        method: "POST",
        body: formData,
        headers: { Accept: "application/json" },
      });

      if (res.ok) {
        setStatus("success");
        setEmail("");
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <PageTransition>
      <div className="min-h-screen bg-bg-main text-text-pure flex flex-col font-sans selection:bg-gold/30 selection:text-gold-light">
        <Header />

        <main className="flex-grow pt-24 md:pt-32 pb-20">
          
          {/* Si está publicado y tiene contenido real, se muestra la cuadrícula activa */}
          {publicado && childrenRealContent ? (
            <div className="max-w-7xl mx-auto px-6 md:px-12">
              {childrenRealContent}
            </div>
          ) : (
            <div className="max-w-5xl mx-auto px-6 md:px-12">
              
              {/* HERO DE SECCIÓN */}
              <FadeIn>
                <section className="text-center flex flex-col items-center pt-8 pb-16">
                  <div className="inline-flex items-center gap-2 border border-gold/35 bg-gold/5 px-4 py-1.5 rounded-full mb-6">
                    <span className="w-2 h-2 rounded-full bg-gold animate-pulse" />
                    <span className="text-[10px] md:text-xs font-mono tracking-widest text-gold uppercase font-medium">
                      En Producción · Próximamente
                    </span>
                  </div>

                  <h1 className="font-serif text-4xl sm:text-6xl md:text-7xl tracking-wide font-light mb-6">
                    {titulo}
                  </h1>

                  <p className="text-text-muted text-base sm:text-lg md:text-xl font-light max-w-2xl mx-auto leading-relaxed mb-6">
                    {subtitulo}
                  </p>

                  <div className="w-16 h-[1px] bg-gold/50 my-4" />
                </section>
              </FadeIn>

              {/* TEMAS ANUNCIADOS */}
              <FadeIn delay={0.15}>
                <section className="mb-20">
                  <div className="text-center mb-8">
                    <span className="text-[10px] font-mono tracking-widest text-gold/80 uppercase">
                      Índice Preliminar de Temas
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {temas.map((tema, i) => (
                      <div
                        key={i}
                        className="p-6 rounded-xl border border-gold/15 bg-bg-secondary/30 hover:border-gold/35 transition-all text-left flex items-start gap-4"
                      >
                        <span className="font-serif text-xl text-gold font-light mt-0.5">
                          {["I", "II", "III", "IV", "V", "VI"][i] || i + 1}
                        </span>
                        <div>
                          <h3 className="font-serif text-base sm:text-lg text-text-pure font-light">
                            {tema}
                          </h3>
                          <span className="text-[10px] font-mono text-text-muted tracking-wider uppercase block mt-1">
                            Material en preparación
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              </FadeIn>

              {/* FORMULARIO AVÍSAME CUANDO SE PUBLIQUE + BOTÓN YOUTUBE */}
              <FadeIn delay={0.25}>
                <section className="p-8 sm:p-12 rounded-2xl border border-gold/25 bg-[#080b15] shadow-2xl relative text-center">
                  <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(197,160,89,0.06),transparent_70%)] pointer-events-none" />

                  <span className="text-[10px] font-mono tracking-widest text-gold uppercase block mb-2">
                    Acceso Temprano
                  </span>
                  <h2 className="font-serif text-2xl sm:text-3xl text-text-pure font-light mb-4">
                    Avísame cuando se publique
                  </h2>
                  <p className="text-text-muted text-xs sm:text-sm font-light max-w-md mx-auto mb-8 leading-relaxed">
                    Te enviaremos una notificación directa tan pronto como el primer contenido de esta sección esté disponible en la plataforma.
                  </p>

                  {status === "success" ? (
                    <div className="p-6 rounded-xl bg-gold/10 border border-gold/30 text-gold text-sm max-w-md mx-auto animate-fade-in">
                      ✓ ¡Listo! Te notificaremos a tu correo en cuanto se estrene este espacio.
                    </div>
                  ) : (
                    <form onSubmit={handleNotifySubmit} className="max-w-md mx-auto space-y-4 text-left">
                      <input
                        type="text"
                        name="_honey"
                        value={honeypot}
                        onChange={(e) => setHoneypot(e.target.value)}
                        className="hidden"
                        tabIndex={-1}
                      />

                      <div className="flex flex-col sm:flex-row gap-3">
                        <input
                          type="email"
                          placeholder="Tu correo electrónico"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                          className="flex-grow px-4 py-3 bg-bg-main border border-gold/20 focus:border-gold text-text-pure text-sm outline-none rounded font-light"
                        />
                        <button
                          type="submit"
                          disabled={status === "loading"}
                          className="bg-gold text-bg-main hover:bg-gold-light px-6 py-3 font-sans font-semibold text-xs uppercase tracking-widest rounded whitespace-nowrap transition-all disabled:opacity-50"
                        >
                          {status === "loading" ? "Guardando..." : "Avísame"}
                        </button>
                      </div>

                      <label className="flex items-start gap-2 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={aceptaPolitica}
                          onChange={(e) => setAceptaPolitica(e.target.checked)}
                          required
                          className="mt-0.5 h-3.5 w-3.5 rounded border-gold/30 bg-bg-main text-gold focus:ring-gold"
                        />
                        <span className="text-[11px] text-text-muted font-light leading-relaxed">
                          Acepto la{" "}
                          <Link href="/privacidad" className="text-gold underline hover:text-gold-light">
                            política de privacidad
                          </Link>{" "}
                          (Ley N.° 29733 de Protección de Datos Personales del Perú).
                        </span>
                      </label>
                    </form>
                  )}

                  {/* Botón a YouTube */}
                  <div className="mt-10 pt-8 border-t border-gold/10 flex flex-col sm:flex-row items-center justify-center gap-4">
                    <span className="text-xs text-text-muted font-light">
                      Mientras tanto, suscríbete al canal para ver los primeros avances en video:
                    </span>
                    <a
                      href={siteConfig.redes.youtube}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 border border-gold/40 bg-gold/5 hover:bg-gold/15 text-gold text-xs font-mono uppercase px-5 py-2.5 rounded tracking-wider whitespace-nowrap transition-colors"
                    >
                      <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                      </svg>
                      <span>Seguir Canal de YouTube</span>
                    </a>
                  </div>
                </section>
              </FadeIn>

            </div>
          )}
        </main>

        <Footer />
      </div>
    </PageTransition>
  );
};
